import React from 'react';
import { UseFormReturn, useFieldArray, Path } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { FormField, FormItem, FormControl } from '@components/ui/form';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { Label } from '@components/ui/label';
import {
  HealthQuestion,
  HealthOption,
} from '@features/onboarding/components/6-health-question-form/interfaces';
import { EXCLUDED_TEXTAREA_LABELS } from 'src/features/onboarding/components/6-health-question-form/constants';
import { GroupedCheckboxGroup } from './GroupedCheckboxGroup';
import { OtherHealthConditions } from './OtherHealthConditions';
import { useHealthStore } from 'src/store/health';

interface FieldValue {
  value: string;
  groupKey?: string;
  details?: string;
}

interface HealthConditionsSliderStepProps {
  questions: HealthQuestion[];
  activeTab: number;
  form: UseFormReturn<HealthQuestionsSchema>;
  errorMessage?: string | null;
  setStepErrorMessage: (msg: string | null) => void;
}

export const HealthConditionsSliderStep: React.FC<
  HealthConditionsSliderStepProps
> = ({ questions, activeTab, form, errorMessage, setStepErrorMessage }) => {
  const { issues, issueDetails } = useHealthStore();

  const fieldArrays = {
    surgeriesDiabetesHeartMental: useFieldArray({
      control: form.control,
      name: 'healthQuestions.surgeriesDiabetesHeartMental.conditions',
    }),
    medDetails: useFieldArray({
      control: form.control,
      name: 'healthQuestions.medDetails.medications',
    }),
    otherHealthConditions: useFieldArray({
      control: form.control,
      name: 'healthQuestions.otherHealthConditions.conditions',
    }),
  };

  const noneValuesMap: { [groupKey: string]: string } = {};
  const currentQuestion = questions[activeTab];
  const groupedOptions = currentQuestion
    ? (currentQuestion.options as HealthOption[][])
    : [];

  issues.forEach((group) => {
    noneValuesMap[group.key] = `${group.key}-none`;
  });

  const getHiddenGroups = (groupedOptions: HealthOption[][]) => {
    const allValues =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) ?? [];
    return groupedOptions.reduce<number[]>((acc, group, idx) => {
      const firstOption = group.find(
        (opt) => typeof opt === 'object' && !opt.isNoneApply
      );
      if (!firstOption) return acc;
      const detail = issueDetails.find((d) => d.key === firstOption.value);
      const key = issues.find((v) => v.id === detail?.issueId)?.key;
      if (!key) return acc;
      const found = allValues.some((item: FieldValue) => item.value === key);
      if (!found) acc.push(idx);
      return acc;
    }, []);
  };

  const renderSection = (question: HealthQuestion) => {
    if (question.type === 'checkbox') {
      // Narrow the field array by question id to avoid union type conflicts
      const { fields, append, remove, replace } =
        question.id === 'surgeriesDiabetesHeartMental'
          ? fieldArrays.surgeriesDiabetesHeartMental
          : question.id === 'medDetails'
            ? fieldArrays.medDetails
            : question.id === 'otherHealthConditions'
              ? fieldArrays.otherHealthConditions
              : fieldArrays.surgeriesDiabetesHeartMental;

      const selectedOptions = fields.map((field: FieldValue) => field.value);
      const isGrouped =
        Array.isArray(question.options) && Array.isArray(question.options[0]);

      const getKey = (groupIdx: number) => {
        if (!groupedOptions[groupIdx]?.find) return '';
        const firstOption = groupedOptions[groupIdx].find(
          (opt) => typeof opt === 'object' && !opt.isNoneApply
        );
        if (!firstOption) return '';

        const detail = issueDetails.find((d) => d.key === firstOption.value);
        const _key = issues.find((v) => v.id === detail?.issueId)?.key;
        return _key || '';
      };
      const groupedOptionsWithType = groupedOptions.map((g, groupIdx) => {
        if (!g.map) return g;
        return g.map((e) => ({ ...e, groupKey: getKey(groupIdx) }));
      });

      if (isGrouped) {
        const hidden = getHiddenGroups(groupedOptions);
        const handleRemoveGroup = (groupIdx: number) => {
          const key = getKey(groupIdx);
          if (!key) return;

          const allValues =
            form.getValues(
              'healthQuestions.surgeriesDiabetesHeartMental.conditions'
            ) ?? [];
          const index = allValues.findIndex((item) => item.value === key);
          if (index >= 0) {
            fieldArrays.surgeriesDiabetesHeartMental.remove(index);
          }
        };

        return (
          <>
            <p className="text-[15px] lg:text-xl leading-6 lg:leading-7 mb-10 max-w-170">
              {question.description}
            </p>
            <div className="flex flex-col gap-6">
              {groupedOptionsWithType.map((group, groupIdx) => {
                if (hidden.includes(groupIdx)) return null;

                const groupKey = getKey(groupIdx);
                const noneValue = noneValuesMap[groupKey];
                const groupPrefix = groupKey || `group-${groupIdx}`;

                const noneOption = group.find((opt) => opt.isNoneApply);
                const checkboxOptions = issueDetails
                  .filter(
                    (e) =>
                      e.issueId ===
                        issues.find((issue) => issue.key === groupKey)?.id &&
                      (issues.find((issue) => issue.key === groupKey)?.id !==
                        4 ||
                        e.type !== 2)
                  )
                  .map((e, index) => ({
                    key: `g${groupIdx}-${groupKey || 'k'}-${e.key ?? index}`,
                    value: `${groupPrefix}-${e.key}`,
                    label: e.label,
                  }));
                if (noneOption && noneValue) {
                  checkboxOptions.push({
                    key: `${groupKey}-none`,
                    value: noneValue,
                    label: noneOption.label,
                  });
                }

                const groupValues = group.map((opt) => opt.value);
                const uniqueGroupValues = [
                  ...groupValues.map((value) => `${groupPrefix}-${value}`),
                  noneValue,
                ].filter(Boolean);

                // Filter fields for this group - handle both API format (just key) and UI format (groupPrefix-key)
                const groupFields = fields.filter((field) => {
                  const fieldValue = field.value;

                  // Check if field matches the group prefix format
                  if (uniqueGroupValues.includes(fieldValue)) {
                    return true;
                  }
                  // Check if field is just the key (API format) and belongs to this group
                  if (groupKey && fieldValue === groupKey) {
                    return true;
                  }
                  // Check if field is a detail option for this group
                  const detail = issueDetails.find((d) => d.key === fieldValue);
                  if (
                    detail &&
                    issues.find((issue) => issue.id === detail.issueId)?.key ===
                      groupKey
                  ) {
                    return true;
                  }
                  return false;
                });

                return (
                  <GroupedCheckboxGroup
                    key={groupIdx}
                    group={group}
                    groupIdx={groupIdx}
                    groupKey={groupKey}
                    fields={groupFields}
                    allFields={fields}
                    append={append}
                    remove={remove}
                    questionId={question.id}
                    onRemoveGroup={() => handleRemoveGroup(groupIdx)}
                    checkboxOptions={checkboxOptions}
                    groupPrefix={groupPrefix}
                    form={form}
                  />
                );
              })}
            </div>
          </>
        );
      }

      const isFlat =
        Array.isArray(question.options) && !Array.isArray(question.options[0]);

      if (isFlat) {
        const flatOptions = question.options as (string | HealthOption)[];
        const noneOption = flatOptions.find(
          (opt) => typeof opt !== 'string' && (opt as HealthOption)?.isNoneApply
        );
        const noneValue =
          typeof noneOption === 'string' ? noneOption : noneOption?.value;
        const isNoneSelected = noneValue ? selectedOptions.includes(noneValue) : false;
        const visibleOptions = selectedOptions.filter(
          (option) => !EXCLUDED_TEXTAREA_LABELS.includes(option)
        );

        return (
          <OtherHealthConditions
            groupedOptions={groupedOptions}
            options={flatOptions.sort((a: string | HealthOption, b: string | HealthOption) => {
              const labelA = typeof a === 'string' ? a : a.label;
              const labelB = typeof b === 'string' ? b : b.label;
              return labelA.localeCompare(labelB);
            })}
            fields={fields}
            append={append}
            remove={remove}
            replace={replace}
            questionId={question.id}
            errorMessage={errorMessage}
            selectedOptions={selectedOptions}
            noneValue={noneValue}
            isNoneSelected={isNoneSelected}
            visibleOptions={visibleOptions}
            form={form}
            description={question.description}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      }
    }

    if (question.type === 'takingMedicationsNow') {
      return (
        <div>
          <p className="text-base mb-4">{question.description}</p>
          <FormField
            control={form.control}
            name={`healthQuestions.${question.id}.value` as Path<HealthQuestionsSchema>}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    onValueChange={(value) => field.onChange(value === 'yes')}
                    value={field.value === true ? 'yes' : field.value === false ? 'no' : undefined}
                    className="flex items-center gap-x-4"
                  >
                    <div className="flex items-center space-x-2 cursor-pointer">
                      <RadioGroupItem value="yes" id={`${question.id}-yes`} />
                      <Label
                        htmlFor={`${question.id}-yes`}
                        className="text-black cursor-pointer"
                      >
                        YES
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 cursor-pointer">
                      <RadioGroupItem value="no" id={`${question.id}-no`} />
                      <Label
                        htmlFor={`${question.id}-no`}
                        className="text-black cursor-pointer"
                      >
                        NO
                      </Label>
                    </div>
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      );
    }

    return null;
  };

  const currentTabQuestion = questions[activeTab];
  if (!currentTabQuestion) {
    return null;
  }

  return (
    <section className="m-0 lg:p-6 p-0 lg:card-shadow">
      <h3 className="text-xl lg:text-2xl font-bold mb-2">
        {currentTabQuestion.question}
      </h3>
      {renderSection(currentTabQuestion)}
    </section>
  );
};
